import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, MapPin, ListChecks } from "lucide-react";
import YoutubePlayer from "./YoutubePlayer";
import MediosPhonePlayer from "./MediosPhonePlayer";
import { TILE_ICON_SIZE } from "./GreenIconTile";
import { ResolveCmsIcon } from "./ResolveCmsIcon";
import data from "../data/content.json";
import { useSiteHome } from "../hooks/useSiteHome";
import { normalizeHeroMedios } from "../lib/youtubeIds";

function splitHubTitle(hub) {
  const highlight = String(hub?.titleHighlight || "").trim();
  const title = String(hub?.title || "").trim();
  if (highlight) return { main: title, highlight };
  const idx = title.toLowerCase().lastIndexOf(" y ");
  if (idx > 12) {
    return { main: title.slice(0, idx), highlight: title.slice(idx + 1) };
  }
  return { main: title, highlight: "" };
}

function TopicPager({ index, total, onPrev, onNext }) {
  return (
    <div className="flex items-center gap-3 border border-border p-2 rounded-full bg-surface shadow-sm shrink-0">
      <button
        type="button"
        onClick={onPrev}
        className="p-2 hover:bg-brand-soft rounded-full transition-all active:scale-90 cursor-pointer text-brand-primary"
        aria-label="Anterior tema"
      >
        <ChevronLeft size={20} />
      </button>
      <span className="font-display font-bold px-4 min-w-[5.5rem] text-center text-brand-dark">
        {index + 1} / {total}
      </span>
      <button
        type="button"
        onClick={onNext}
        className="p-2 hover:bg-brand-soft rounded-full transition-all active:scale-90 cursor-pointer text-brand-primary"
        aria-label="Siguiente tema"
      >
        <ChevronRight size={20} />
      </button>
    </div>
  );
}

const KnowledgeHub = () => {
  const { data: home } = useSiteHome();
  const knowledgeHub = home?.knowledgeHub || data.knowledgeHub;
  const { topics } = knowledgeHub;
  const medios = normalizeHeroMedios(home?.hero?.medios || data.hero?.medios);
  const showMediosPhone = medios.clips.length > 0;
  const [index, setIndex] = useState(0);
  const { main: titleMain, highlight: titleHighlight } = splitHubTitle(knowledgeHub);

  const nextTopic = () => setIndex((prev) => (prev + 1) % Math.max(topics.length, 1));
  const prevTopic = () =>
    setIndex((prev) => (prev - 1 + topics.length) % Math.max(topics.length, 1));

  const currentTopic = topics[index] || topics[0];
  if (!currentTopic) return null;

  const recs = currentTopic.recommendations || [];
  const topicVideo = currentTopic.videoUrl;

  return (
    <section id="conocimiento" className="relative z-[15] py-24 md:py-32 bg-white overflow-visible">
      <div className="container mx-auto px-6 overflow-visible">
        <div className="flex flex-col gap-12 lg:flex-row lg:items-stretch lg:gap-0">
          <div
            className={`min-w-0 ${
              showMediosPhone
                ? "lg:flex-1 lg:pr-8 xl:pr-10 lg:border-r lg:border-brand-dark/15"
                : "w-full"
            }`}
          >
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 lg:mb-14">
              <div className="min-w-0 max-w-3xl">
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-8 h-[2px] bg-brand-accent shrink-0" />
                  <span className="text-brand-primary font-body font-bold uppercase tracking-[0.3em] text-[11px]">
                    {knowledgeHub.badge}
                  </span>
                </div>
                <h2 className="font-display text-[1.75rem] sm:text-4xl xl:text-[2.75rem] font-black text-brand-dark leading-[1.12] tracking-tighter">
                  {titleMain}
                  {titleHighlight ? (
                    <>
                      <br />
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-mid italic font-semibold pb-1 pr-2 box-decoration-clone inline-block leading-[1.25]">
                        {titleHighlight}
                      </span>
                    </>
                  ) : null}
                </h2>
              </div>
              <TopicPager
                index={index}
                total={topics.length}
                onPrev={prevTopic}
                onNext={nextTopic}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-5 xl:gap-6 items-stretch">
              <motion.div
                key={`side-${currentTopic.id}`}
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.45 }}
                className="w-full md:w-fit md:max-w-full md:shrink-0 bg-brand-dark rounded-[28px] p-5 text-white shadow-2xl flex flex-col relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand-accent/5 blur-3xl rounded-full pointer-events-none" />
                <div className="relative z-10 flex flex-col h-full">
                  <p className="text-brand-accent italic text-[11px] uppercase tracking-widest mb-2 font-bold font-body whitespace-normal md:whitespace-nowrap">
                    Investigación Especializada
                  </p>
                  <div className="w-full md:w-0 md:min-w-full flex flex-col flex-1 min-h-0">
                    <h3 className="text-xl font-display font-medium mb-3 leading-tight">
                      {currentTopic.title}
                    </h3>
                    <p className="text-white/80 text-[13px] leading-relaxed mb-5 font-body">
                      {currentTopic.subtitle}
                    </p>

                    <div className="space-y-3">
                      {topicVideo ? (
                        recs.map((rec, i) => (
                          <motion.div
                            key={`${currentTopic.id}-g-${i}`}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.08 }}
                            className="p-3.5 rounded-2xl bg-white/5 border border-white/10"
                          >
                            <div className="flex items-center gap-3 mb-1 min-w-0">
                              <ResolveCmsIcon
                                icon={rec.icon}
                                size={18}
                                className="text-brand-accent shrink-0"
                                alt={rec.label}
                              />
                              <span className="font-bold text-[12px] text-brand-accent uppercase font-body tracking-wide leading-snug">
                                {rec.label}
                              </span>
                            </div>
                            <p className="text-white/90 text-[12px] pl-7 leading-snug font-medium">
                              {rec.desc}
                            </p>
                          </motion.div>
                        ))
                      ) : currentTopic.description ? (
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="text-white text-sm font-bold italic leading-relaxed border-l-2 border-brand-accent pl-3.5 py-1"
                        >
                          {currentTopic.description}
                        </motion.p>
                      ) : null}
                    </div>

                    {currentTopic.event ? (
                      <div className="mt-6 flex items-start gap-3 text-brand-accent text-[10px] font-bold uppercase tracking-widest bg-brand-accent/10 p-3.5 rounded-xl border border-brand-accent/20 font-body">
                        <MapPin size={14} className="shrink-0 mt-0.5" />
                        <span className="leading-snug">{currentTopic.event}</span>
                      </div>
                    ) : null}

                    <div className="mt-auto pt-8 flex items-center gap-4">
                      <ListChecks size={18} className="text-brand-accent shrink-0" />
                      <div className="h-px flex-grow bg-white/20" />
                    </div>
                  </div>
                </div>
              </motion.div>

              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <AnimatePresence mode="wait">
                  {topicVideo ? (
                    <motion.div
                      key={`video-${currentTopic.id}`}
                      initial={{ opacity: 0, scale: 0.98, y: 16 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.98, y: -16 }}
                      transition={{ duration: 0.45 }}
                      className="relative aspect-video w-full rounded-[28px] xl:rounded-[40px] overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.28)] border-[5px] border-white bg-black"
                    >
                      <YoutubePlayer url={topicVideo} title={currentTopic.title} />
                    </motion.div>
                  ) : (
                    <motion.div
                      key={`grid-${currentTopic.id}`}
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -16 }}
                      className="grid grid-cols-1 sm:grid-cols-2 gap-4 xl:gap-6 h-full"
                    >
                      {recs.map((rec, i) => (
                        <motion.div
                          key={`${currentTopic.id}-${i}`}
                          whileHover={{ y: -4 }}
                          className="flex items-start gap-4 p-5 xl:p-7 bg-surface border border-border rounded-[28px] xl:rounded-[40px] min-w-0"
                        >
                          <div className="w-12 h-12 xl:w-16 xl:h-16 rounded-2xl bg-white shadow-md flex items-center justify-center shrink-0">
                            <ResolveCmsIcon
                              icon={rec.icon}
                              size={TILE_ICON_SIZE}
                              alt={rec.label}
                            />
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-bold text-brand-dark text-sm xl:text-lg mb-1 uppercase tracking-tight leading-snug">
                              {rec.label}
                            </h4>
                            <p className="text-[#6b7c72] text-[13px] xl:text-[15px] leading-relaxed font-medium">
                              {rec.desc}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
                <div className="h-10 mt-3 flex items-center justify-center">
                  {topicVideo ? (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-brand-dark/40 text-[10px] font-bold uppercase tracking-[0.4em] font-body text-center"
                    >
                      Protocolo de Vigilancia Acreditado
                    </motion.p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {showMediosPhone ? (
            <aside
              className={`w-full lg:w-auto lg:self-start shrink-0 lg:pl-8 xl:pl-10 flex justify-center ${
                medios.desbordar ? "medios-aside medios-aside--overflow" : "medios-aside"
              }`}
            >
              <MediosPhonePlayer
                clips={medios.clips}
                title={medios.tituloReproductor}
                posterUrl={home?.hero?.posterUrl || data.hero?.posterUrl}
                width={medios.reproductorAncho}
                height={medios.reproductorAlto}
                marcoColor={medios.marcoColor}
                marcoAncho={medios.marcoAncho}
              />
            </aside>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default KnowledgeHub;
